'use client';

import { KeyboardEvent, useState } from 'react';
import { T } from '@/lib/tokens';

type Props = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  label?: string;
  helper?: string;
};

/**
 * Tab on an empty textarea fills with the placeholder — kept from prototype
 * (it's a researcher convenience for filling example text).
 */
export function Textarea({
  value,
  onChange,
  placeholder,
  rows = 5,
  label,
  helper,
}: Props) {
  const [focused, setFocused] = useState(false);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && !value.trim() && placeholder) {
      e.preventDefault();
      onChange(placeholder);
    }
  };

  const showHint = focused && !value.trim() && placeholder;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {label && (
        <label style={{ fontSize: 14, fontWeight: 500, color: T.espresso }}>
          {label}
        </label>
      )}
      {helper && (
        <span style={{ fontSize: 13, color: T.espressoLight }}>{helper}</span>
      )}
      <div style={{ position: 'relative' }}>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: '100%',
            resize: 'vertical',
            border: focused
              ? `1.5px solid ${T.terra}`
              : `1.5px solid ${T.creamBorder}`,
            borderRadius: 10,
            padding: '14px 16px',
            fontFamily: 'inherit',
            fontSize: 15,
            lineHeight: 1.6,
            color: T.espresso,
            background: T.white,
            outline: 'none',
            transition: 'border-color 0.15s',
          }}
        />
        {showHint && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 12,
              fontSize: 11,
              color: T.espressoLight,
              background: T.creamMid,
              borderRadius: 4,
              padding: '2px 7px',
              pointerEvents: 'none',
              border: `1px solid ${T.creamBorder}`,
            }}
          >
            Tab ↹ to fill example
          </div>
        )}
      </div>
    </div>
  );
}
